import { signInWithCustomToken, signOut } from 'firebase/auth';
import { firebaseAuth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

/**
 * Bridges the Cognito session into a Firebase Auth session so that
 * firestore.rules can enforce tenant access (request.auth.token.organizationId).
 * Mirrors the mobile app's firebaseAuthBridge.
 */

let signInPromise: Promise<void> | null = null;

export async function ensureFirebaseSignIn(): Promise<void> {
  if (firebaseAuth.currentUser) return;
  if (signInPromise) return signInPromise;

  signInPromise = (async () => {
    const cognitoIdToken = localStorage.getItem('idToken');
    if (!cognitoIdToken) {
      throw new Error('No Cognito session to bridge');
    }

    // Bound the request so a stalled backend can never hang sign-in forever.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/firebase-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${cognitoIdToken}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok) {
      throw new Error(`Firebase token exchange failed: ${response.status}`);
    }

    const { firebaseToken } = await response.json();
    await signInWithCustomToken(firebaseAuth, firebaseToken);
    console.log('[firebaseAuthBridge] Firebase session established');
  })().finally(() => {
    signInPromise = null;
  });

  return signInPromise;
}

export async function firebaseSignOut(): Promise<void> {
  try {
    await signOut(firebaseAuth);
  } catch (err) {
    console.warn('[firebaseAuthBridge] Sign out failed:', err);
  }
}
